/**
 * UserBehaviorTable Component - Phase 2C-4
 *
 * Table of user behavioral metrics.
 * BSL Compliant: Factual data only, neutral styling.
 */

import { Card, CardHeader, CardTitle, CardContent, LoadingSpinner, Button } from '../common';
import { UserBehavior } from '../../api';

interface UserBehaviorTableProps {
  users: UserBehavior[];
  loading?: boolean;
  error?: string | null;
  selectedUserId?: string | null;
  onSelectUser?: (userId: string | null) => void;
}

export function UserBehaviorTable({
  users,
  loading,
  error,
  selectedUserId,
  onSelectUser,
}: UserBehaviorTableProps): JSX.Element {
  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <LoadingSpinner size="lg" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-slate-400">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!users.length) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-slate-400">No user data available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>User Behavioral Metrics</CardTitle>
          {selectedUserId && onSelectUser && (
            <Button variant="ghost" size="sm" onClick={() => onSelectUser(null)}>
              Clear Selection
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">User</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Events</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Avg Time</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Avg Stake</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Accuracy</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Streak</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr
                  key={user.user_id}
                  className={`border-b border-slate-800 cursor-pointer transition-colors ${
                    selectedUserId === user.user_id
                      ? 'bg-blue-900/20'
                      : 'hover:bg-slate-800/50'
                  }`}
                  onClick={() => onSelectUser?.(user.user_id)}
                >
                  <td className="py-3 px-4">
                    <span className="font-mono text-slate-200">{user.user_id}</span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <span className="font-mono text-slate-300">{user.total_stake_events}</span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <span className="font-mono text-slate-300">
                      {user.avg_decision_time_seconds.toFixed(1)}s
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <span className="font-mono text-slate-300">
                      {user.avg_stake_percentage.toFixed(1)}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <span className="font-mono text-slate-300">
                      {(user.overall_accuracy * 100).toFixed(1)}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <span className="font-mono text-slate-300">{user.current_streak}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}